import { Box, Paper, Popper, Typography } from '@mui/material'
import type { ScaleLinear, ScaleTime } from 'd3-scale'
import type { DateTime } from 'luxon'
import type { RefObject } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { X_AXIS_HEIGHT } from '@/features/bidding/components/cascading/charts/constants'
import AreaGraph from '@/features/bidding/components/cascading/charts/elements/AreaGraph'
import type { AreaGraphElement } from '@/features/bidding/components/cascading/charts/types'
import { getMarketColor } from '@/features/bidding/constants/marketColors'
import type { LevelVolume } from '@/features/bidding/hooks/useHourlyVolumeData'
import { useHourlyVolumeData } from '@/features/bidding/hooks/useHourlyVolumeData'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { ForecastMarketPair } from '@/features/bidding/types/forecastPricing'
import { getMarketProgramNames } from '@/features/bidding/utils/forecast/marketProgramNames'

type HourlyVolumeChartProps = {
  bidLevelInputs: BidLevelInput[] | BidLevelInputSymmetrical[]
  selectedDeliveryDay: DateTime
  market: ForecastMarketPair
  height?: number
  aggregateAvailability?: AggregateAvailabilityResponse | null
  aggregateSuggestedBids?: AggregateSuggestedBidResponse | null
}

const DEFAULT_HEIGHT = 250

function HourlyVolumeChart({
  bidLevelInputs,
  selectedDeliveryDay,
  market,
  height = DEFAULT_HEIGHT,
  aggregateAvailability,
  aggregateSuggestedBids,
}: Readonly<HourlyVolumeChartProps>) {
  const { t } = useTranslation()
  const chartRef = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<TooltipState>(null)
  const [hiddenOverlays, setHiddenOverlays] = useState<Set<string>>(new Set())

  // Convert Luxon DateTime to JS Date for the hook
  const baseDate = useMemo(() => selectedDeliveryDay.startOf('day').toJSDate(), [selectedDeliveryDay])

  // White stroke to create visual separation between stacked levels
  const layerStroke = useCallback(() => 'white', [])

  const { data, datetimes, layerKeys, layerFill, overlayLines } = useHourlyVolumeData({
    levels: bidLevelInputs,
    market,
    baseDate,
    aggregateSuggestedBids,
    aggregateAvailability,
  })

  // Handle hover events from the chart
  const handleHover = useCallback((position: { x: number; y: number } | null, dataPoint: AreaGraphElement | null) => {
    if (!position || !dataPoint) {
      setTooltip(null)
      return
    }
    const levelVolumes = dataPoint.meta.levelVolumes as LevelVolume[] | undefined
    if (!levelVolumes) {
      setTooltip(null)
      return
    }
    const overlayValues: Pick<
      NonNullable<TooltipState>,
      'suggestedUpMW' | 'suggestedDownMW' | 'availableUpMW' | 'availableDownMW'
    > = {}

    if ('suggested_up' in dataPoint.layerData) {
      overlayValues.suggestedUpMW = dataPoint.layerData.suggested_up
    }
    if ('suggested_down' in dataPoint.layerData) {
      overlayValues.suggestedDownMW = Math.abs(dataPoint.layerData.suggested_down)
    }
    if ('available_up' in dataPoint.layerData) {
      overlayValues.availableUpMW = dataPoint.layerData.available_up
    }
    if ('available_down' in dataPoint.layerData) {
      overlayValues.availableDownMW = Math.abs(dataPoint.layerData.available_down)
    }

    setTooltip({
      position,
      hour: dataPoint.meta.hour as number,
      levelVolumes,
      ...overlayValues,
    })
  }, [])

  // Check if there are any assigned groups (regardless of their capacity)
  // This is different from checking totalVolume > 0, because groups with 0 MW capacity
  // should still show the chart with 0-value bars.
  // D1 suggested bids also count as data worth rendering.
  const toggleOverlay = useCallback((prefix: string) => {
    setHiddenOverlays((prev) => {
      const next = new Set(prev)
      if (next.has(prefix)) {
        next.delete(prefix)
      } else {
        next.add(prefix)
      }
      return next
    })
  }, [])

  const legendHeight = 24
  const chartHeight = height - legendHeight

  const effectiveOverlayLines = useMemo(
    () =>
      overlayLines.map((l) => {
        const prefix = l.key.startsWith('suggested') ? 'suggested' : 'available'
        if (hiddenOverlays.has(prefix)) {
          return { ...l, opacity: 0 }
        }
        return l
      }),
    [overlayLines, hiddenOverlays],
  )

  // Render red-ish background for PTUs where allocated volume exceeds available volume
  const renderOverAllocationOverlay = useCallback(
    ({ timeScale }: { timeScale: ScaleTime<number, number>; valueScale: ScaleLinear<number, number> }) => {
      const rects: React.ReactNode[] = []
      const svgHeight = chartHeight - X_AXIS_HEIGHT

      const barInterval = datetimes.length >= 2 ? timeScale(datetimes[1]) - timeScale(datetimes[0]) : 0

      for (let i = 0; i < data.length; i++) {
        if (!data[i].meta.overAllocated) continue

        const x = timeScale(datetimes[i])
        const nextX = i < datetimes.length - 1 ? timeScale(datetimes[i + 1]) : x + barInterval
        const width = nextX - x

        rects.push(
          <rect key={`overalloc-${i}`} fill="#fca5a5" height={svgHeight} opacity={0.35} width={width} x={x} y={0} />,
        )
      }

      return rects.length > 0 ? <g>{rects}</g> : null
    },
    [data, datetimes, chartHeight],
  )

  // Check if there are any assigned groups (regardless of their capacity)
  // This is different from checking totalVolume > 0, because groups with 0 MW capacity
  // should still show the chart with 0-value bars.
  // D1 suggested bids also count as data worth rendering.
  const hasAssignedGroups = bidLevelInputs.some((level) => level.assignedGroups.length > 0)
  const hasVolumeCapacity = bidLevelInputs.some((level) => {
    const asDirectional = level as BidLevelInput
    const asSymmetrical = level as BidLevelInputSymmetrical
    return (
      (asDirectional.upVolumeCapacity?.value != null && asDirectional.upVolumeCapacity.value > 0) ||
      (asDirectional.downVolumeCapacity?.value != null && asDirectional.downVolumeCapacity.value > 0) ||
      (asSymmetrical.volumeCapacity?.value != null && asSymmetrical.volumeCapacity.value > 0)
    )
  })
  const hasSuggestedBids = (aggregateSuggestedBids?.suggestions?.length ?? 0) > 0
  const hasAvailability = (aggregateAvailability?.availabilities?.length ?? 0) > 0

  if (!hasAssignedGroups && !hasVolumeCapacity && !hasSuggestedBids && !hasAvailability) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.50',
          borderRadius: 1,
        }}
      >
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
          {t('bidding.bidding_cascading.charts.no_volume_data')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box ref={chartRef} sx={{ width: '100%', height, px: 2 }}>
      {/* Legend header */}
      <Box
        sx={{
          height: legendHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: 'text.secondary',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {overlayLines.some((l) => l.key.startsWith('suggested')) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                opacity: hiddenOverlays.has('suggested') ? 0.4 : 1,
              }}
              onClick={() => toggleOverlay('suggested')}
            >
              <Box component="svg" sx={{ width: 16, height: 12 }} viewBox="0 0 16 12">
                <line stroke="#1e293b" strokeDasharray="4 2" strokeWidth={1.5} x1={0} x2={16} y1={6} y2={6} />
              </Box>
              <span>{t('bidding.bidding_cascading.charts.suggested')}</span>
            </Box>
          )}
          {overlayLines.some((l) => l.key.startsWith('available')) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                opacity: hiddenOverlays.has('available') ? 0.4 : 1,
              }}
              onClick={() => toggleOverlay('available')}
            >
              <Box component="svg" sx={{ width: 16, height: 12 }} viewBox="0 0 16 12">
                <line stroke="#0284c7" strokeDasharray="4 2" strokeWidth={1.5} x1={0} x2={16} y1={6} y2={6} />
              </Box>
              <span>{t('bidding.bidding_cascading.charts.available')}</span>
            </Box>
          )}
        </Box>
        <span>MW</span>
      </Box>
      <AreaGraph
        markerAtZero
        showGridLines
        barLabels={{
          formatValue: (v) => v.toFixed(1),
          minBarHeight: 16,
          minBarWidth: 20,
        }}
        barPadding={0.15}
        data={data}
        datetimes={datetimes}
        formatTick={(v) => `${Math.abs(v).toFixed(0)}`}
        height={`${chartHeight}px`}
        layerFill={layerFill}
        layerKeys={layerKeys}
        layerStroke={layerStroke}
        overlayLines={effectiveOverlayLines.length > 0 ? effectiveOverlayLines : undefined}
        renderOverlay={renderOverAllocationOverlay}
        testId="hourly-volume-chart"
        onHover={handleHover}
      />

      <HourlyVolumeTooltip
        chartRef={chartRef}
        datetimesCount={datetimes.length}
        legendHeight={legendHeight}
        market={market}
        selectedDeliveryDay={selectedDeliveryDay}
        tooltip={tooltip}
      />
    </Box>
  )
}

type TooltipState = {
  position: { x: number; y: number }
  hour: number
  levelVolumes: LevelVolume[]
  suggestedUpMW?: number
  suggestedDownMW?: number
  availableUpMW?: number
  availableDownMW?: number
} | null

type HourlyVolumeTooltipProps = {
  tooltip: TooltipState
  chartRef: RefObject<HTMLDivElement>
  datetimesCount: number
  selectedDeliveryDay: DateTime
  market: ForecastMarketPair
  legendHeight: number
}

function HourlyVolumeTooltip({
  tooltip,
  chartRef,
  datetimesCount,
  selectedDeliveryDay,
  market,
  legendHeight,
}: Readonly<HourlyVolumeTooltipProps>) {
  const { t } = useTranslation()
  const marketColor = getMarketColor(market)
  const marketLabel = getMarketProgramNames(market).displayName

  const placement = useMemo((): 'left' | 'right' => {
    if (!tooltip || !chartRef.current) return 'left'
    const rect = chartRef.current.getBoundingClientRect()
    const chartMidpoint = rect.left + rect.width / 2
    return tooltip.position.x < chartMidpoint ? 'left' : 'right'
  }, [tooltip, chartRef])

  const modifiers = useMemo(
    () => [
      {
        name: 'offset' as const,
        options: {
          offset: () => {
            if (!chartRef.current || datetimesCount === 0) return [0, 0]
            const chartWidth = chartRef.current.getBoundingClientRect().width
            const barHalfWidth = ((chartWidth / datetimesCount) * (1 - 0.15)) / 2
            return [0, Math.round(barHalfWidth)]
          },
        },
      },
      { name: 'flip' as const, enabled: false },
    ],
    [datetimesCount, chartRef],
  )

  return (
    <Popper
      anchorEl={
        tooltip && chartRef.current
          ? (() => {
              const chart = chartRef.current
              return {
                getBoundingClientRect: () => {
                  const chartRect = chart.getBoundingClientRect()
                  const chartCenterY = chartRect.top + legendHeight + (chartRect.height - legendHeight) / 2
                  return {
                    top: chartCenterY,
                    left: tooltip.position.x,
                    bottom: chartCenterY,
                    right: tooltip.position.x,
                    width: 0,
                    height: 0,
                    x: tooltip.position.x,
                    y: chartCenterY,
                    toJSON: () => ({}),
                  }
                },
              }
            })()
          : null
      }
      modifiers={modifiers}
      open={tooltip !== null}
      placement={placement}
      sx={{ zIndex: 1500, pointerEvents: 'none' }}
    >
      {tooltip && (
        <Paper
          elevation={0}
          sx={{
            p: 1,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
          }}
        >
          {/* Tooltip header with date and market */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 0.5,
              pb: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600 }}>
              {selectedDeliveryDay.toFormat('dd MMM yyyy')} {String(tooltip.hour).padStart(2, '0')}:00
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 0.5,
                  backgroundColor: marketColor,
                }}
              />
              <Typography sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>{marketLabel}</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `auto repeat(${tooltip.levelVolumes.length + 1}, auto)`,
              columnGap: 1.5,
              rowGap: 0.25,
              alignItems: 'center',
            }}
          >
            {/* Header row */}
            <Typography sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>MW</Typography>
            {tooltip.levelVolumes.map((level) => (
              <Typography key={level.levelId} sx={{ fontSize: '0.5625rem', fontWeight: 600, color: 'text.secondary' }}>
                L{level.levelIndex + 1}
              </Typography>
            ))}
            <Typography sx={{ fontSize: '0.5625rem', fontWeight: 600, color: 'text.secondary' }}>Total</Typography>

            {/* UP row */}
            <Typography sx={{ fontSize: '0.625rem', color: 'success.main' }}>UP</Typography>
            {tooltip.levelVolumes.map((level) => (
              <Typography key={level.levelId} sx={{ fontSize: '0.625rem', textAlign: 'right' }}>
                {level.upMW.toFixed(1)}
              </Typography>
            ))}
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, textAlign: 'right' }}>
              {tooltip.levelVolumes.reduce((sum, l) => sum + l.upMW, 0).toFixed(1)}
            </Typography>

            {/* DOWN row */}
            <Typography sx={{ fontSize: '0.625rem', color: 'error.main' }}>DOWN</Typography>
            {tooltip.levelVolumes.map((level) => (
              <Typography key={level.levelId} sx={{ fontSize: '0.625rem', textAlign: 'right' }}>
                {level.downMW.toFixed(1)}
              </Typography>
            ))}
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, textAlign: 'right' }}>
              {tooltip.levelVolumes.reduce((sum, l) => sum + l.downMW, 0).toFixed(1)}
            </Typography>
          </Box>

          {/* Suggested / Available overlay values */}
          {(tooltip.suggestedUpMW != null || tooltip.availableUpMW != null) && (
            <Box
              sx={{
                mt: 0.5,
                pt: 0.5,
                borderTop: 1,
                borderColor: 'divider',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr 1fr',
                columnGap: 1.5,
                rowGap: 0.25,
                alignItems: 'center',
              }}
            >
              {/* Header row */}
              <Box />
              <Typography sx={{ fontSize: '0.5625rem', fontWeight: 600, color: 'success.main', textAlign: 'right' }}>
                UP
              </Typography>
              <Typography sx={{ fontSize: '0.5625rem', fontWeight: 600, color: 'error.main', textAlign: 'right' }}>
                DOWN
              </Typography>

              {/* Suggested row */}
              {tooltip.suggestedUpMW != null && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="svg" sx={{ width: 12, height: 8 }} viewBox="0 0 12 8">
                      <line stroke="#1e293b" strokeDasharray="4 2" strokeWidth={1.5} x1={0} x2={12} y1={4} y2={4} />
                    </Box>
                    <Typography sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>
                      {t('bidding.bidding_cascading.charts.suggested')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.625rem', textAlign: 'right' }}>
                    {tooltip.suggestedUpMW.toFixed(1)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.625rem', textAlign: 'right' }}>
                    {(tooltip.suggestedDownMW ?? 0).toFixed(1)}
                  </Typography>
                </>
              )}

              {/* Available row */}
              {tooltip.availableUpMW != null && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="svg" sx={{ width: 12, height: 8 }} viewBox="0 0 12 8">
                      <line stroke="#0284c7" strokeDasharray="4 2" strokeWidth={1.5} x1={0} x2={12} y1={4} y2={4} />
                    </Box>
                    <Typography sx={{ fontSize: '0.625rem', color: 'text.secondary' }}>
                      {t('bidding.bidding_cascading.charts.available')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.625rem', textAlign: 'right' }}>
                    {tooltip.availableUpMW.toFixed(1)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.625rem', textAlign: 'right' }}>
                    {(tooltip.availableDownMW ?? 0).toFixed(1)}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Popper>
  )
}

export default HourlyVolumeChart
